/**
 * Viewport Search Plugin for Search Results Map
 *
 * Single place that decides how the search follows the map. On every settled map
 * position it builds one immutable snapshot (camera, gesture, location type, result
 * type, derived mode), asks the ordered policies for a decision, and translates the
 * one winning decision into the existing Redux actions. The first policy that returns
 * a non-null decision wins; when none applies, the neutral fallback leaves focused mode
 * if it was active and otherwise does nothing. Removing a policy from the list never
 * breaks the page; that behavior simply stops.
 *
 * The core also owns:
 *  - the single map gesture handler (one flag, consumed after every settled position);
 *  - lifecycle events forwarded to every policy's `onEvent` (start, location selected,
 *    backend bounds, location-type, State identity and result-type changes), whose
 *    returned intents it executes in order;
 *  - the minimum zoom for each location type.
 *
 * Policies are pure (services/*.policy.js) and never touch the store,
 * the map or the DOM.
 *
 * This plugin does NOT:
 * - Manage the "search within map" button, its click or "near me" (see searchWithinMap.plugin)
 * - Create or configure the map instance (see map.plugin)
 * - Re-center on a new search origin or reset zoom per location type (see mapViewport.plugin)
 *
 * @module searchResultsMap/plugins/viewportSearch.plugin
 */

import {
  distinctUntilChanged,
  debounceTime,
  filter,
  map,
  pairwise,
  skip,
} from 'rxjs';
import {
  searchParametersActions,
  selectLocation,
  selectLocationType,
  selectResultType,
  selectFocusedSearchActive,
} from 'features/searchResultsFacets';
import { selectGeoCoordinates, selectSitePlans } from '../redux/selectors';
import { locationSelected$, mapBoundariesUpdated$ } from '../redux/observables';
import { areBoundsValid } from '../services/geometry';
import createFetchLocationsPayload from '../services/rectangularAreaSearchPayload';
import createMapGestureHandlers from '../handlers/mapGestureHandlers';
import buildSnapshot from '../services/buildSnapshot';
import decideViewportSearch from '../services/decideViewportSearch';
import getMinZoomForLocationType from '../services/modeCameraConstraints';
import createStateViewportPayload from '../services/stateViewportPayload';
import ViewportSearchDecisionType from '../enums/viewportSearchDecisionType';
import ViewportSearchIntentType from '../enums/viewportSearchIntentType';
import ViewportSearchEvent from '../enums/viewportSearchEvent';
import {
  DEFAULT_MIN_ZOOM,
  RECTANGULAR_AREA,
  STATE,
} from '../constants';

const normalizeBackendBounds = ({ minLatitude, maxLatitude, minLongitude, maxLongitude } = {}) => ({
  south: minLatitude,
  north: maxLatitude,
  west: minLongitude,
  east: maxLongitude,
});

/**
 * Identifies a State by fields that stay stable across viewport refreshes but change
 * when the user selects a different state from the search bar.
 */
const getStateIdentityKey = (location) =>
  [location?.stateName, location?.marketState, location?.originLatitude, location?.originLongitude].join('|');

/**
 * @param {object} [options]
 * @param {Array<{ name: string, evaluate: Function, onEvent?: Function }>} [options.policies]
 *   Ordered list of viewport search policies. Order is priority.
 */
export default function createViewportSearchPlugin({ policies = [] } = {}) {
  const state = {
    started: false,
    subscriptions: [],
    mapFacade: null,
    store$: null,
    dispatch: null,
    getState: null,
    policies: [...policies],
    gestureHandlers: createMapGestureHandlers(),
    hasUserGesture: false,
    isInitialPosition: true,
    currentMinZoom: DEFAULT_MIN_ZOOM,
  };

  function isFocusedSearchActive() {
    return selectFocusedSearchActive(state.getState());
  }

  function dispatchFocusedBounds(bounds) {
    state.dispatch(searchParametersActions.setFocusedSearchBounds({
      south: bounds.south,
      north: bounds.north,
      west: bounds.west,
      east: bounds.east,
    }));
  }

  function enterFocused(bounds) {
    if (!isFocusedSearchActive()) {
      state.dispatch(searchParametersActions.setFocusedSearchActive(true));
    }
    dispatchFocusedBounds(bounds);
  }

  function exitFocused() {
    if (!isFocusedSearchActive()) {
      return;
    }
    state.dispatch(searchParametersActions.setFocusedSearchActive(false));
    state.dispatch(searchParametersActions.clearFocusedSearchBounds());
  }

  function searchRectangle(bounds) {
    const payload = createFetchLocationsPayload(bounds);
    if (!payload) {
      return;
    }

    // Leaving focused mode for a rectangle happens in handleLocationTypeTransition,
    // which turns the flag off without clearing the rectangle's own bounds.
    state.dispatch(searchParametersActions.fetchLocations(payload));
  }

  function refreshState(bounds) {
    const payload = createStateViewportPayload(selectLocation(state.getState()), bounds);
    if (!payload) {
      return;
    }

    // The State payload overwrites the shared lat/lng fields, so focused mode is
    // turned off without clearing them; the refresh stays a single request.
    if (isFocusedSearchActive()) {
      state.dispatch(searchParametersActions.setFocusedSearchActive(false));
    }

    state.dispatch(searchParametersActions.fetchMapLocations(payload));
  }

  function applyDecision(decision) {
    switch (decision?.type) {
      case ViewportSearchDecisionType.EnterFocused:
        enterFocused(decision.bounds);
        break;
      case ViewportSearchDecisionType.UpdateFocused:
        dispatchFocusedBounds(decision.bounds);
        break;
      case ViewportSearchDecisionType.ExitFocused:
        exitFocused();
        break;
      case ViewportSearchDecisionType.SearchRectangle:
        searchRectangle(decision.bounds);
        break;
      case ViewportSearchDecisionType.RefreshState:
        refreshState(decision.bounds);
        break;
      default:
        break;
    }
  }

  function setMinZoomNow(minZoom) {
    state.currentMinZoom = minZoom;
    state.mapFacade?.setMapMinZoom?.(minZoom);
  }

  /**
   * Lowering the minimum zoom applies at once, so a policy's zoom-out intent in the
   * same transition is not clamped by the previous type's limit. Raising it waits for
   * the next idle, so the camera is not forced to jump while it is still settling.
   */
  function applyMinZoomForLocationType(locationType) {
    const minZoom = getMinZoomForLocationType(locationType);

    if (minZoom <= state.currentMinZoom) {
      setMinZoomNow(minZoom);
      return;
    }

    state.mapFacade?.onceMapEvent?.('idle', () => {
      setMinZoomNow(minZoom);
    });
  }

  function executeIntent(intent) {
    switch (intent?.type) {
      case ViewportSearchIntentType.Recenter:
        state.mapFacade?.updateMapCenter?.({ latitude: intent.latitude, longitude: intent.longitude });
        break;
      case ViewportSearchIntentType.SetZoom:
        state.mapFacade?.setMapZoom?.(intent.zoom);
        break;
      case ViewportSearchIntentType.FitBounds:
        state.mapFacade?.fitBounds?.(intent.bounds);
        break;
      case ViewportSearchIntentType.GrantGesture:
        state.hasUserGesture = true;
        break;
      case ViewportSearchIntentType.DeferUntilIdle:
        state.mapFacade?.onceMapEvent?.('idle', () => {
          intent.intents.forEach(executeIntent);
        });
        break;
      case ViewportSearchIntentType.DispatchDecision:
        applyDecision(intent.decision);
        break;
      default:
        break;
    }
  }

  function executeIntents(intents) {
    (intents ?? []).forEach(executeIntent);
  }

  function emitEvent(type, payload = {}) {
    const event = Object.freeze({ type, ...payload });
    state.policies.forEach((policy) => {
      executeIntents(policy?.onEvent?.(event));
    });
  }

  function handleSettledPosition() {
    const currentState = state.getState();
    const snapshot = buildSnapshot({
      camera: state.mapFacade?.getCameraSnapshot?.(),
      hasUserGesture: state.hasUserGesture,
      isInitialPosition: state.isInitialPosition,
      locationType: selectLocationType(currentState),
      resultType: selectResultType(currentState),
      focusedSearchActive: selectFocusedSearchActive(currentState),
      location: selectLocation(currentState),
      sitePlans: selectSitePlans(currentState),
    });

    if (snapshot) {
      applyDecision(decideViewportSearch(state.policies, snapshot));
    }

    // Each settled position needs its own fresh gesture: a drag from several moves
    // ago must not authorize a later, unrelated programmatic camera move.
    state.hasUserGesture = false;
    state.isInitialPosition = false;
  }

  function subscribeToSettledPositions() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectGeoCoordinates),
        filter(areBoundsValid),
        distinctUntilChanged(),
        debounceTime(300),
      ).subscribe(handleSettledPosition),
    );
  }

  function handleLocationSelected({ latitude, longitude } = {}) {
    state.hasUserGesture = false;
    state.isInitialPosition = true;
    emitEvent(ViewportSearchEvent.LocationSelected, { latitude, longitude });
  }

  function subscribeToLocationSelected() {
    state.subscriptions.push(
      locationSelected$.subscribe(handleLocationSelected),
    );
  }

  function handleBoundsFromBackend(data) {
    const bounds = data?.bounds ? normalizeBackendBounds(data.bounds) : null;
    emitEvent(ViewportSearchEvent.BoundsFromBackend, {
      bounds: areBoundsValid(bounds) ? bounds : null,
      locationType: selectLocationType(state.getState()),
    });
  }

  function subscribeToBoundsFromBackend() {
    state.subscriptions.push(
      mapBoundariesUpdated$.subscribe(handleBoundsFromBackend),
    );
  }

  function handleLocationTypeTransition([from, to]) {
    if (from === RECTANGULAR_AREA) {
      // Leaving a rectangle starts a new search context; a gesture made while
      // dragging the rectangle must not authorize anything for the next location.
      state.hasUserGesture = false;
    }

    if (to === RECTANGULAR_AREA && isFocusedSearchActive()) {
      // fetchLocations has already written the rectangle's bounds into the shared
      // lat/lng fields; clearing them here would null out the rectangle.
      state.dispatch(searchParametersActions.setFocusedSearchActive(false));
    }

    applyMinZoomForLocationType(to);
    emitEvent(ViewportSearchEvent.LocationTypeChanged, { from, to, location: selectLocation(state.getState()) });
  }

  function subscribeToLocationTypeChange() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectLocationType),
        distinctUntilChanged(),
        pairwise(),
      ).subscribe(handleLocationTypeTransition),
    );
  }

  function isStateLocation(currentState) {
    return selectLocationType(currentState) === STATE;
  }

  function selectStateIdentityKey(currentState) {
    return getStateIdentityKey(selectLocation(currentState));
  }

  function handleStateChanged() {
    emitEvent(ViewportSearchEvent.StateChanged, { location: selectLocation(state.getState()) });
  }

  function subscribeToStateChange() {
    state.subscriptions.push(
      state.store$.pipe(
        filter(isStateLocation),
        map(selectStateIdentityKey),
        distinctUntilChanged(),
        skip(1),
      ).subscribe(handleStateChanged),
    );
  }

  function handleResultTypeTransition([from, to]) {
    emitEvent(ViewportSearchEvent.ResultTypeChanged, { from, to });
  }

  function subscribeToResultTypeChange() {
    state.subscriptions.push(
      state.store$.pipe(
        map(selectResultType),
        distinctUntilChanged(),
        pairwise(),
      ).subscribe(handleResultTypeTransition),
    );
  }

  function onUserGesture() {
    state.hasUserGesture = true;
  }

  function clearSubscriptions() {
    state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
    state.subscriptions = [];
  }

  return {
    name: 'viewport-search',

    async init(context) {
      context.state.viewportSearch = { started: false };
    },

    async start(context) {
      if (state.started) {
        return;
      }

      state.mapFacade = context.services.mapFacade;
      state.store$ = context.services.store$;
      state.dispatch = context.services.dispatch;
      state.getState = context.services.getState;

      if (!state.mapFacade || !state.store$ || !state.dispatch || !state.getState) {
        return;
      }

      state.gestureHandlers.bind(state.mapFacade, context.root, { onUserGesture });
      state.hasUserGesture = false;
      state.isInitialPosition = true;

      subscribeToSettledPositions();
      subscribeToLocationSelected();
      subscribeToBoundsFromBackend();
      subscribeToLocationTypeChange();
      subscribeToStateChange();
      subscribeToResultTypeChange();

      const currentState = state.getState();
      const locationType = selectLocationType(currentState);
      setMinZoomNow(getMinZoomForLocationType(locationType));
      emitEvent(ViewportSearchEvent.Start, { location: selectLocation(currentState), locationType });

      state.started = true;
      context.state.viewportSearch = { started: true };
    },

    async stop(context) {
      clearSubscriptions();
      state.gestureHandlers.unbind();

      if (state.started && isFocusedSearchActive()) {
        state.dispatch(searchParametersActions.setFocusedSearchActive(false));
        state.dispatch(searchParametersActions.clearFocusedSearchBounds());
      }

      state.started = false;
      state.mapFacade = null;
      state.store$ = null;
      state.dispatch = null;
      state.getState = null;
      state.hasUserGesture = false;
      state.isInitialPosition = true;
      state.currentMinZoom = DEFAULT_MIN_ZOOM;

      delete context.state.viewportSearch;
    },
  };
}
